import { useEffect, useState } from "react";
import { Loader2, MessageCircleQuestion, HelpCircle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";

const NAMES = { landing: "Visitor tour", user: "User tour", pro: "Pro tour", admin: "Admin tour" };

function TourCard({ t }) {
  const max = Math.max(1, ...t.steps.map((s) => s.views));
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid={`tour-analytics-${t.tour}`}>
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="font-semibold text-slate-900">{NAMES[t.tour]}</h3>
        <span className="text-xs text-slate-500">{t.starts} start{t.starts === 1 ? "" : "s"} · <b className="text-emerald-700" data-testid={`tour-completion-${t.tour}`}>{t.completion}%</b> finished · {t.spanish_starts} in Spanish</span>
      </div>
      <div className="mt-4 space-y-1.5">
        {t.steps.map((s) => (
          <div key={s.idx} className="grid grid-cols-12 items-center gap-2 text-xs" data-testid={`tour-step-stat-${t.tour}-${s.idx}`}>
            <span className="col-span-4 truncate text-slate-600" title={s.title}>{s.idx + 1}. {s.title}</span>
            <div className="col-span-5 h-2 bg-slate-100 rounded-full"><div className="h-2 rounded-full bg-emerald-500" style={{ width: `${(s.views / max) * 100}%` }} /></div>
            <span className="col-span-1 text-right text-slate-700">{s.views}</span>
            <span className="col-span-1 text-right text-amber-700" title="Replays">{s.replays ? `↺${s.replays}` : ""}</span>
            <span className="col-span-1 text-right text-red-600" title="Closed the tour here">{s.skipped_here ? `✕${s.skipped_here}` : ""}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function TourAnalytics() {
  const [days, setDays] = useState("30");
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get("/admin/tour-analytics", { params: { days } }).then((r) => setD(r.data)); }, [days]);
  return (
    <div data-testid="tour-analytics">
      <div className="flex items-center gap-3 mb-3">
        <p className="text-sm text-slate-500 flex-1">Views per step, replays (↺) and where people closed the tour early (✕). Visitors are counted anonymously.</p>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-40" data-testid="tour-analytics-days-select"><SelectValue /></SelectTrigger>
          <SelectContent>{[["7", "Last 7 days"], ["30", "Last 30 days"], ["90", "Last 90 days"], ["365", "Last year"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {!d ? <Loader2 className="h-5 w-5 animate-spin text-emerald-600" /> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {d.tours.map((t) => <TourCard key={t.tour} t={t} />)}
          <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="tour-top-questions">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2"><MessageCircleQuestion className="h-4 w-4 text-amber-500" />Most asked questions <span className="text-xs font-normal text-slate-500">({d.total_questions} total)</span></h3>
            {d.questions.length === 0 ? <p className="text-sm text-slate-500 mt-3">No questions yet.</p> : (
              <ol className="mt-3 space-y-1.5 text-sm">{d.questions.map((q, i) => <li key={i} className="flex gap-2" data-testid={`tour-question-${i}`}><span className="text-slate-400 w-5">{i + 1}.</span><span className="flex-1 text-slate-700">{q.question}</span><b className="text-slate-900">{q.count}</b></li>)}</ol>
            )}
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="tour-help-taps">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2"><HelpCircle className="h-4 w-4 text-emerald-600" />"Explain this page" taps</h3>
            {d.help.length === 0 ? <p className="text-sm text-slate-500 mt-3">No taps yet.</p> : (
              <ul className="mt-3 space-y-1.5 text-sm">{d.help.map((h) => <li key={h.route} className="flex justify-between"><span className="font-mono text-xs text-slate-600">{h.route}</span><b>{h.taps}</b></li>)}</ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
